'use client';

import { c as _c } from "react/compiler-runtime";
import SvgManual from '@common/icon/SvgManual';
import { cn } from '@core/utils';
import type { DetailHomeInstitutionResponse, ListDonationBusinessResponse, ListHomeGiftOrderProductReviewResponse, ListHomeInstitutionContentResponse } from '@generated/unauth';
import type { GiftProductList } from '@/api/getGiftProductList';
import { CustomLink } from '@/app/_components/CustomLink';
import { DONATION_DESKTOP_STICKY_TOP } from '@/app/donation/constants';
import { PageNavigation } from '@/components/client';
import { DONATION_ROUTES } from '@/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useEventListTotalCount } from '../_hooks/useEventListTotalCount';
import { HOMETOWNS_DETAIL_TAB } from '../constants';
import { AppBack } from './AppBack';
import { BenefitsContent } from './BenefitsContent';
import { ContentsContent } from './ContentsContent';
import { EmptyState } from './EmptyState';
import { GiftsContent } from './GiftsContent';
import { HometownsTab } from './HometownsTab';
import { MainInfoSection } from './MainInfoSection';
import { PolicyContent } from './PolicyContent';
import { ProjectsContent } from './ProjectsContent';
import { ReviewsContent } from './ReviewsContent';
import { Section } from './Section';
const SECTION_CLASS_NAME = cn('mb-80 px-40 content-container', 'mobile-tablet:mb-20 mobile-tablet:px-20');
interface HometownsDetailTemplateProps {
  institutionId: number;
  institutionData: DetailHomeInstitutionResponse;
  giftProductData?: GiftProductList;
  donationBusinessData?: ListDonationBusinessResponse;
  institutionContentData?: ListHomeInstitutionContentResponse;
  hasPolicy: boolean;
  giftReviewData?: ListHomeGiftOrderProductReviewResponse;
  isKakaobank: boolean;
}
export const HometownsDetailTemplate = t0 => {
  const $ = _c(51);
  const {
    institutionId,
    institutionData,
    giftProductData,
    donationBusinessData,
    institutionContentData,
    hasPolicy,
    giftReviewData,
    isKakaobank
  } = t0;
  const t1 = !isKakaobank;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      enabled: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    data: benefitsCount,
    isPending: isBenefitsPending
  } = useEventListTotalCount(institutionId, t2);
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const hasGifts = (giftProductData?.totalCount ?? 0) > 0;
  const hasProjects = (donationBusinessData?.totalCount ?? 0) > 0;
  const hasContents = (institutionContentData?.totalCount ?? 0) > 0;
  const hasReviews = (giftReviewData?.totalCount ?? 0) > 0;
  const hasBenefits = !isKakaobank && (benefitsCount ?? 0) > 0;
  let t3;
  if ($[2] !== giftProductData || $[3] !== hasGifts || $[4] !== institutionId) {
    t3 = hasGifts && giftProductData ? {
      tab: HOMETOWNS_DETAIL_TAB.GIFTS,
      title: "\uB2F5\uB840\uD488",
      content: <GiftsContent institutionId={institutionId} initialData={giftProductData.contents} totalCount={giftProductData.totalCount ?? 0} />
    } : null;
    $[2] = giftProductData;
    $[3] = hasGifts;
    $[4] = institutionId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== donationBusinessData || $[7] !== hasProjects || $[8] !== institutionId) {
    t4 = hasProjects && donationBusinessData ? {
      tab: HOMETOWNS_DETAIL_TAB.PROJECTS,
      title: "\uC9C0\uC815\uAE30\uBD80",
      content: <ProjectsContent institutionId={institutionId} initialData={donationBusinessData.contents} />
    } : null;
    $[6] = donationBusinessData;
    $[7] = hasProjects;
    $[8] = institutionId;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== hasContents || $[11] !== institutionContentData || $[12] !== institutionId) {
    t5 = hasContents && institutionContentData ? {
      tab: HOMETOWNS_DETAIL_TAB.CONTENTS,
      title: "\uCF58\uD150\uCE20",
      content: <ContentsContent institutionId={institutionId} initialData={institutionContentData.contents} />
    } : null;
    $[10] = hasContents;
    $[11] = institutionContentData;
    $[12] = institutionId;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== hasPolicy || $[15] !== institutionId) {
    t6 = hasPolicy ? {
      tab: HOMETOWNS_DETAIL_TAB.POLICY,
      title: "\uC815\uCC45",
      content: <PolicyContent institutionId={institutionId} />
    } : null;
    $[14] = hasPolicy;
    $[15] = institutionId;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== giftReviewData || $[18] !== hasReviews || $[19] !== institutionId) {
    t7 = hasReviews && giftReviewData ? {
      tab: HOMETOWNS_DETAIL_TAB.REVIEWS,
      title: "\uD6C4\uAE30",
      content: <ReviewsContent institutionId={institutionId} initialData={giftReviewData.contents} />
    } : null;
    $[17] = giftReviewData;
    $[18] = hasReviews;
    $[19] = institutionId;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== hasBenefits || $[22] !== institutionId) {
    t8 = hasBenefits ? {
      tab: HOMETOWNS_DETAIL_TAB.BENEFITS,
      title: "\uD61C\uD0DD",
      content: <BenefitsContent institutionId={institutionId} />
    } : null;
    $[21] = hasBenefits;
    $[22] = institutionId;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t3 || $[25] !== t4 || $[26] !== t5 || $[27] !== t6 || $[28] !== t7 || $[29] !== t8) {
    t9 = [t3, t4, t5, t6, t7, t8].flatMap(_temp);
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  const sections = t9;
  const hasNoSectionToShow = sections.length === 0 && (isKakaobank || !isBenefitsPending);
  let t10;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <AppBack />;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <PageNavigation donationHomeButton={true} isNotUsedHomeButton={true}><CustomLink href={DONATION_ROUTES.GUIDE.pathname} aria-label={"\uAC00\uC774\uB4DC \uD398\uC774\uC9C0\uB85C \uC774\uB3D9"}><SvgManual className="size-24 text-Gray-900" /></CustomLink></PageNavigation>;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== institutionData.backgroundVideoImageUrl || $[34] !== institutionData.description || $[35] !== institutionData.institutionName || $[36] !== institutionData.logoUrl || $[37] !== institutionData.pcBackgroundVideoImageUrl || $[38] !== institutionData.summary || $[39] !== institutionId || $[40] !== isKakaobank) {
    t12 = <MainInfoSection title={institutionData.institutionName} summary={institutionData.summary} description={institutionData.description} institutionId={institutionId} backgroundVideoImageUrl={institutionData.backgroundVideoImageUrl} pcBackgroundVideoImageUrl={institutionData.pcBackgroundVideoImageUrl} logoUrl={institutionData.logoUrl} isKakaobank={isKakaobank} />;
    $[33] = institutionData.backgroundVideoImageUrl;
    $[34] = institutionData.description;
    $[35] = institutionData.institutionName;
    $[36] = institutionData.logoUrl;
    $[37] = institutionData.pcBackgroundVideoImageUrl;
    $[38] = institutionData.summary;
    $[39] = institutionId;
    $[40] = isKakaobank;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== isDesktopViewport || $[43] !== sections) {
    t13 = sections.length > 0 ? <><header className={cn("sticky z-10 mt-40 px-40 content-container", "mobile-tablet:top-48 mobile-tablet:mt-20 mobile-tablet:px-0")} style={isDesktopViewport ? {
        top: DONATION_DESKTOP_STICKY_TOP
      } : undefined}><HometownsTab tabs={sections.map(_temp2)} /></header>{sections.map(_temp3)}</> : null;
    $[42] = isDesktopViewport;
    $[43] = sections;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== hasNoSectionToShow) {
    t14 = hasNoSectionToShow ? <EmptyState className={SECTION_CLASS_NAME} /> : null;
    $[45] = hasNoSectionToShow;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] !== t12 || $[48] !== t13 || $[49] !== t14) {
    t15 = <div className="relative">{t10}{t11}{t12}{t13}{t14}</div>;
    $[47] = t12;
    $[48] = t13;
    $[49] = t14;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  return t15;
};
function _temp(section) {
  return section ? [section] : [];
}
function _temp2(t0) {
  const {
    tab,
    title
  } = t0;
  return {
    tab,
    title
  };
}
function _temp3(t0) {
  const {
    tab: tab_0,
    content
  } = t0;
  return <Section key={tab_0} id={tab_0} className={SECTION_CLASS_NAME}>{content}</Section>;
}